import {
  PreparedDynamicStructure,
  DynamicPropertyType,
  FieldNames,
  FieldSchema,
  FieldsSubscriptions,
  FieldValues,
  Generator,
  Schema,
  StateGenerator,
  FieldName,
  areValuesDifferent,
  prepareDynamicPropertyValue
} from "../Schema";
import { cloneDeep } from "lodash";


export interface BasicState {
  loading: boolean;
}

export interface CustomState extends BasicState {};

export type FieldsState<S extends BasicState, K extends FieldNames = FieldNames> = Record<K[number], S>;

export type Mapping<S extends BasicState> = Partial<{
  [K in keyof S]?: DynamicPropertyType<S[K]>;
}>;

/**
 * Manager template for preparing and using Schema for specific tasks.
 *
 * @template S - The type of the state object that will be used by Manager
 *               and generated by Schema.
 */
export abstract class DynamicManager<S extends BasicState, C extends CustomState = CustomState> {
  /**
   * Flag indicating whether the manager is currently processing state updates.
   */
  loading: boolean = true;

  /**
   * Initial values of fields before any updates, used for comparison in dynamic structures.
   */
  initialValues: FieldValues = {};

  /**
   * Schema that the manager is working with.
   */
  schema: Schema = {};

  /**
   * Subscriptions for fields managed by the SchemaManager.
   */
  subscriptions: FieldsSubscriptions = {};

  /**
   * State of the fields managed by the SchemaManager.
   *
   * @template S - The type of the state object that will be used by Manager
   *               and generated by Schema.
   */
  state: FieldsState<S> = {};

  /**
   * Generator for state updates based on schema and field values.
   *
   * @template S - The type of the state object that will be used by Manager
   *               and generated by Schema.
   */
  stateGenerator: StateGenerator<S> = {};

  /**
   * Custom state management for specific use cases.
   */
  abstract customState: C;

  /**
   * @param schema - Schema that the manager is working with.
   * @protected
   */
  constructor(schema: Schema) {
    this.schema = cloneDeep(schema);
    this.prepareStateGenerator();
  }

  /**
   * Update the custom state based on the current general state.
   */
  abstract updateCustomState(): void;

  /**
   * Get a customized mapping for a specific field"s state generation.
   *
   * @param field - Field schema to get mapping for.
   * @template S - The type of the state object that will be used by Manager
   *               and generated by Schema.
   * @returns Mapping object of the state of the field.
   */
  abstract getMapping(field: FieldSchema): Mapping<S>;

  /**
   * Initialize the manager with initial field values.
   * 
   * @param initialValues - Initial values for the fields.
   * @returns Promise that resolves to manager itself when the initialization is complete.
   */
  async init(initialValues: FieldValues) {
    this.initialValues = cloneDeep(initialValues);
    await this.updateState(
      this.initialValues,
      true
    );
    await this.setLoading(false);
    return this;
  }

  /**
   * Set the loading state of the manager.
   *
   * @param loading - Whether the manager is loading or not.
   */
  async setLoading(loading: boolean) {
    this.loading = loading;
    this.updateCustomState();
  }

  /**
   * Register subscriptions for a field to other fields.
   *
   * @param fieldName - Name of the field to register subscriptions for.
   * @param connectedFieldNames - Names of fields that are connected to the field.
   */
  registerSubscriptions(
    fieldName: string,
    connectedFieldNames: FieldNames
  ) {
    connectedFieldNames.forEach((connectedFieldName) => {
      if (fieldName !== connectedFieldName) {
        this.subscriptions[connectedFieldName] = Array.from(new Set([
          ...(this.subscriptions[connectedFieldName] ?? []),
          fieldName
        ]));
      }
    });
  }

  /**
   * Prepare a state generator for the field based on its mapping.
   *
   * @param field - Field schema to prepare state generator for.
   * @template S - The type of the state object that will be used by Manager
   *               and generated by Schema.
   * @returns Generator for the state of the field.
   */
  prepareFieldStateGenerator(field: FieldSchema): Generator<S> {
    return Object.fromEntries(
      Object.entries(
        this.getMapping(field)
      ).map(
        ([propertyName, value]) => {
          const preparedPropertyValue = prepareDynamicPropertyValue(value);
          this.registerSubscriptions(
            field.name,
            preparedPropertyValue.fields
          );
          return [propertyName, preparedPropertyValue];
        }
      )
    ) as Generator<S>;
  };

  /**
   * Prepare state generators for all fields in the schema.
   */
  prepareStateGenerator() {
    for (const fieldName in this.schema) {
      this.stateGenerator[fieldName] = this.prepareFieldStateGenerator(
        this.schema[fieldName]
      );
    }
  }

  /**
   * Process dynamic structure and return its value based on provided fields values.
   * The cache mechanism can be used to avoid unnecessary calculations or requests.
   * 
   * @param dynamicStructure - Dynamic structure to process.
   * @param values - Values of fields to process the dynamic structure with.
   * @template T - The type of the dynamic structure value.
   * @returns Value of the processed dynamic structure.
   */
  async processDynamicStructure<T>(
    name: string,
    dynamicStructure: PreparedDynamicStructure<T>,
    values: FieldValues
  ): Promise<T> {
    const extractedValues = Object.fromEntries(
      [
        name,
        ...dynamicStructure.fields
      ].map(
        (fieldName) => [fieldName, values[fieldName]]
      )
    );
    if (dynamicStructure.disabledCache) {
      return await dynamicStructure.getter(extractedValues);
    }
    if (
      dynamicStructure.cache?.cachedResult !== undefined &&
      !areValuesDifferent(
        extractedValues,
        dynamicStructure.cache?.fieldValues ?? {}
      )
    ) {
      return dynamicStructure.cache.cachedResult;
    }
    const result = await dynamicStructure.getter(extractedValues);
    dynamicStructure.cache = {
      fieldValues: extractedValues,
      cachedResult: result,
    };
    return result;
  }

  /**
   * Update the state of a specific field based on provided fields values
   * and also checks for field dependencies (subscriptions) and updates them.
   *
   * @param name - Name of the field to update.
   * @param values - Values of fields to update the field with.
   * @param triggerFields - list of fields that triggered the subscription update.
   * @param skipSubscriptions - The flag indicating whether to skip triggering
   *                            validation of subscribed fields.
   * @template S - The type of the state object that will be used by Manager
   *               and generated by Schema.
   * @returns Promise that resolves when the state update is complete.
   */
  async updateFieldState(
    name: FieldName,
    values: FieldValues,
    triggerFields: FieldNames = [],
    skipSubscriptions: boolean = false
  ) {
    const fieldStateGenerator = this.stateGenerator[name];
    const newState = {} as S;
    for (const propertyName in fieldStateGenerator) {
      newState[propertyName] = await this.processDynamicStructure(
        name,
        fieldStateGenerator[propertyName],
        values
      );
    }
    this.setFieldState(
      name,
      {
        ...this.getFieldState(name),
        ...newState
      }
    );
    if (!skipSubscriptions) {
      await this.updateSubscribedFieldStates(
        name,
        values,
        Array.from(new Set([...triggerFields, name])) as FieldNames
      );
    }
  }

  /**
   * Update the state of all fields that are subscribed to the specific field.
   * @param name - Name of the field to update subscribed fields for.
   * @param values - Values of fields to update the subscribed fields with.
   * @param triggerFields - List of fields that triggered the subscription update.
   */
  async updateSubscribedFieldStates(
    name: FieldName,
    values: FieldValues,
    triggerFields: FieldNames,
  ) {
    for (const connectedFieldName of (this.subscriptions[name] ?? [])) {
      if (triggerFields.includes(connectedFieldName)) {
        await this.updateFieldState(connectedFieldName, values, triggerFields, true);
      } else {
        await this.updateFieldState(connectedFieldName, values, triggerFields);
      }
    }
  }

  /**
   * Update the state of all fields based on provided fields values.
   *
   * @param values - Values of fields to update the state with.
   * @param initial - Flag indicating if this is an initial state update.
   * @returns Promise that resolves when the state update is complete.
   */
  async updateState(
    values: FieldValues,
    initial: boolean = false
  ) {
    if (!initial) {
      await this.setLoading(true);
    }
    for (const fieldName in this.stateGenerator) {
      await this.updateFieldState(fieldName, values);
    }
    if (!initial) {
      await this.setLoading(false);
    }
  }

  /**
   * Function to get the state of a specific field.
   *
   * @param name - Name of the field to get the state for.
   * @template S - The type of the state object that will be used by Manager
   *               and generated by Schema.
   */
  setFieldState(
    name: FieldName,
    newState: S
  ) {
    this.state[name] = {
      ...newState,
    };
  }

  /**
   * Function to get the state of a specific field.
   *
   * @param name - Name of the field to get the state for.
   * @template S - The type of the state object that will be used by Manager
   *               and generated by Schema.
   * @returns Deep clone of the field"s state.
   */
  getFieldState(name: FieldName): S {
    return cloneDeep(this.state[name]);
  }

  /**
   * Function to get the entire state of the schema.
   * @template S - The type of the state object that will be used by Manager
   *               and generated by Schema.
   * @returns Deep clone of the entire schema state.
   */
  getState(): FieldsState<S> {
    return cloneDeep(this.state);
  };

  /**
   * Function to get the custom state.
   * @returns Deep clone of the custom state.
   */
  getCustomState(): C {
    return cloneDeep(this.customState);
  }
}
